import { Injectable, OnDestroy } from '@angular/core';
import {
  Action,
  Actions,
  NgxsOnInit,
  ofActionSuccessful,
  Selector,
  State,
  StateContext,
  StateToken,
  Store,
} from '@ngxs/store';
import { catchError, EMPTY, forkJoin, map, of, Subscription, switchMap } from 'rxjs';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  ChangeCurrentSelectedDepartment,
  ChangeCurrentSelectedTenant,
  LoadTenantUserAccess,
} from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { ICartItem } from '../models/cart-item.model';
import { ProductApiService } from '../services/product-api.service';
import {
  AddManyToCart,
  AddToCart,
  ClearCart,
  LoadCart,
  RemoveFromCart,
  UpdateCartItemQuantity,
} from './cart.actions';

export interface CartStateModel {
  items: ICartItem[];
}

interface CartScope {
  tenantId: string;
  userId: string;
  departmentId: string;
}

const CART_STATE_TOKEN = new StateToken<CartStateModel>('cart');
const CART_STORAGE_KEY_PREFIX = 'storageCart';

@State<CartStateModel>({
  name: CART_STATE_TOKEN,
  defaults: {
    items: [],
  },
})
@Injectable()
export class CartState implements NgxsOnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  constructor(
    private store: Store,
    private actions$: Actions,
    private productApiService: ProductApiService
  ) {}

  ngxsOnInit(ctx: StateContext<CartStateModel>): void {
    this.subscriptions.add(
      this.actions$.pipe(ofActionSuccessful(LoadTenantUserAccess)).subscribe(() => {
        ctx.dispatch(new LoadCart());
      })
    );

    this.subscriptions.add(
      this.actions$.pipe(ofActionSuccessful(ChangeCurrentSelectedTenant)).subscribe(() => {
        ctx.dispatch(new LoadCart());
      })
    );

    this.subscriptions.add(
      this.actions$.pipe(ofActionSuccessful(ChangeCurrentSelectedDepartment)).subscribe(() => {
        ctx.dispatch(new LoadCart());
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @Action(LoadCart)
  loadCart(ctx: StateContext<CartStateModel>): void {
    const key = this.getStorageKey();
    if (!key) {
      ctx.patchState({ items: [] });
      return;
    }

    ctx.patchState({ items: this.readItems(key) });
  }

  @Action(AddToCart)
  @OnSuccess({ message: 'addedToCart' })
  addToCart(ctx: StateContext<CartStateModel>, { item }: AddToCart) {
    const key = this.getStorageKey();
    if (!key) {
      ctx.patchState({ items: [] });
      return EMPTY;
    }

    const items = this.readItems(key);
    if (items.some((cartItem) => cartItem.productId === item.productId)) {
      return EMPTY;
    }

    const schematicImage$ = item.schematicImage?.link
      ? of(item.schematicImage)
      : this.productApiService.getProductById(item.productId).pipe(
          map((product) => product.schematicImage),
          catchError(() => of(undefined))
        );

    return schematicImage$.pipe(
      switchMap((schematicImage) => {
        const currentItems = this.readItems(key);
        if (currentItems.some((cartItem) => cartItem.productId === item.productId)) {
          return EMPTY;
        }

        this.patchAndSave(ctx, key, [
          ...currentItems,
          {
            ...item,
            schematicImage,
            quantity: 1,
          },
        ]);
        return of(schematicImage);
      })
    );
  }

  @Action(AddManyToCart)
  @OnSuccess({ message: 'addedSelectedToCart' })
  addManyToCart(ctx: StateContext<CartStateModel>, { items }: AddManyToCart) {
    const key = this.getStorageKey();
    if (!key) {
      ctx.patchState({ items: [] });
      return EMPTY;
    }

    const currentItems = this.readItems(key);
    const uniqueItems = this.getUniqueNewItems(items, currentItems);
    if (uniqueItems.length === 0) {
      return EMPTY;
    }

    return forkJoin(uniqueItems.map((item) => this.withSchematicImage(item))).pipe(
      switchMap((itemsWithImages) => {
        const latestItems = this.readItems(key);
        const latestNewItems = this.getUniqueNewItems(itemsWithImages, latestItems);
        if (latestNewItems.length === 0) {
          return EMPTY;
        }

        this.patchAndSave(ctx, key, [...latestItems, ...latestNewItems]);
        return of(latestNewItems);
      })
    );
  }

  @Action(RemoveFromCart)
  removeFromCart(ctx: StateContext<CartStateModel>, { productId }: RemoveFromCart): void {
    const key = this.getStorageKey();
    if (!key) {
      ctx.patchState({ items: [] });
      return;
    }

    this.patchAndSave(
      ctx,
      key,
      ctx.getState().items.filter((item) => item.productId !== productId)
    );
  }

  @Action(UpdateCartItemQuantity)
  updateCartItemQuantity(
    ctx: StateContext<CartStateModel>,
    { productId, quantity }: UpdateCartItemQuantity
  ): void {
    const key = this.getStorageKey();
    if (!key) {
      ctx.patchState({ items: [] });
      return;
    }

    if (!quantity || quantity < 1) {
      return;
    }

    this.patchAndSave(
      ctx,
      key,
      ctx
        .getState()
        .items.map((item) => (item.productId === productId ? { ...item, quantity } : item))
    );
  }

  @Action(ClearCart)
  clearCart(ctx: StateContext<CartStateModel>): void {
    const key = this.getStorageKey();
    ctx.patchState({ items: [] });
    if (!key) {
      return;
    }

    try {
      localStorage.removeItem(key);
    } catch {
      return;
    }
  }

  @Selector()
  static getItems(state: CartStateModel): ICartItem[] {
    return state.items;
  }

  @Selector()
  static getCount(state: CartStateModel): number {
    return state.items.length;
  }

  @Selector()
  static isInCart(state: CartStateModel): (productId: string) => boolean {
    return (productId: string) => state.items.some((item) => item.productId === productId);
  }

  private patchAndSave(ctx: StateContext<CartStateModel>, key: string, items: ICartItem[]): void {
    if (this.getStorageKey() === key) {
      ctx.patchState({ items });
    }

    try {
      localStorage.setItem(key, JSON.stringify(items));
    } catch {
      return;
    }
  }

  private getUniqueNewItems(items: ICartItem[], currentItems: ICartItem[]): ICartItem[] {
    const existingIds = new Set(currentItems.map((item) => item.productId));
    const idsToAdd = new Set<string>();
    const uniqueItems: ICartItem[] = [];

    items.forEach((item) => {
      if (existingIds.has(item.productId) || idsToAdd.has(item.productId)) {
        return;
      }

      idsToAdd.add(item.productId);
      uniqueItems.push({ ...item, quantity: 1 });
    });

    return uniqueItems;
  }

  private withSchematicImage(item: ICartItem) {
    if (item.schematicImage?.link) {
      return of(item);
    }

    return this.productApiService.getProductById(item.productId).pipe(
      map((product) => ({ ...item, schematicImage: product.schematicImage })),
      catchError(() => of(item))
    );
  }

  private readItems(key: string): ICartItem[] {
    try {
      const items = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  private getStorageKey(): string | null {
    const scope = this.getScope();
    if (!scope || scope.departmentId === ALL_DEPS_TOKEN) {
      return null;
    }

    return `${CART_STORAGE_KEY_PREFIX}_${scope.tenantId}_${scope.userId}_${scope.departmentId}`;
  }

  private getScope(): CartScope | null {
    const tenantId = this.store.selectSnapshot(TenantUserAccessState.currentTenantId);
    const userId = this.store.selectSnapshot(TenantUserAccessState.userAccess)?.id;
    const departmentId = this.store.selectSnapshot(TenantUserAccessState.currentDepartmentId);

    if (!tenantId || !userId || !departmentId) {
      return null;
    }

    return { tenantId, userId, departmentId };
  }
}
